import {
  BrokeredOrderSortOption,
  BrokeredOrderStatus,
} from '@rematter/frontend-lib-api-client';

import { BrokeredOrderRecord } from '../types/brokeredOrder';

import { createQueryAtomFactory } from '~/shared/providers/api/createQueryAtomFactory';
import { parseZonedDateTime } from '~/shared/utils/dateTime';

export const brokeredOrdersListQueryAtom = createQueryAtomFactory(
  'brokerage.brokeredOrdersList',
  (params: {
    ids?: string[];
    searchTerm?: string;
    supplierIds?: string[];
    customerIds?: string[];
    statuses?: BrokeredOrderStatus[];
    limit?: number;
    offset?: number;
    sorting?: BrokeredOrderSortOption[];
  }) => ({
    brokeredOrders: {
      __args: {
        filter: {
          ids: params.ids,
          searchTerm: params.searchTerm,
          supplierIds: params.supplierIds,
          customerIds: params.customerIds,
          statuses: params.statuses,
        },
        sorting: params.sorting,
        pagination: {
          limit: params.limit ?? 20,
          offset: params.offset ?? 0,
        },
      },
      entries: {
        id: true,
        orderNumber: true,
        status: true,
        suppliers: {
          id: true,
          name: true,
        },
        customers: {
          id: true,
          name: true,
        },
        purchaseTotal: {
          amount: true,
          currencyCode: true,
        },
        salesTotal: {
          amount: true,
          currencyCode: true,
        },
        margin: {
          amount: true,
          currencyCode: true,
        },
        marginPercent: true,
        lineItems: {
          id: true,
        },
        timestamps: {
          createdAt: true,
          updatedAt: true,
        },
      },
      page: {
        total: true,
      },
    },
  }),
  {
    select: (
      data,
    ): { brokeredOrders: BrokeredOrderRecord[]; totalCount: number } => {
      if (!data?.brokeredOrders?.entries) {
        return { brokeredOrders: [], totalCount: 0 };
      }

      return {
        brokeredOrders: data.brokeredOrders.entries.map((order) => {
          const firstSupplier = order.suppliers?.[0];
          const firstCustomer = order.customers?.[0];
          return {
            id: order.id,
            orderNumber: order.orderNumber,
            status: order.status as BrokeredOrderStatus,
            supplier: firstSupplier
              ? { id: firstSupplier.id, name: firstSupplier.name }
              : null,
            customer: firstCustomer
              ? { id: firstCustomer.id, name: firstCustomer.name }
              : null,
            purchaseTotal: order.purchaseTotal,
            salesTotal: order.salesTotal,
            margin: order.margin,
            marginPercent: order.marginPercent,
            lineItemCount: order.lineItems?.length ?? 0,
            timestamps: {
              createdAt: parseZonedDateTime(order.timestamps.createdAt),
              updatedAt: parseZonedDateTime(order.timestamps.updatedAt),
            },
          };
        }),
        totalCount: data.brokeredOrders.page?.total ?? 0,
      };
    },
  },
);
